import { useEffect, useState } from "react";
import { ChevronUp, ChevronDown, MapPin, Car, Clock, Zap, GripVertical, ArrowRightLeft } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { moveJob, updateJobStatus, reorderTruckQueue } from "../lib/api";
import { EmptyState } from "./EmptyState";
import { toast } from "sonner";
import { SERVICE_LABEL, STATUS_LABEL, STATUS_COLORS, PRIORITY_COLORS, STATUS_FLOW } from "../lib/constants";
import { formatTimeAgo, cn } from "../lib/utils";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableQueueItem({ job, idx, total, onMove, onAdvance, onReassign, onOpenDetail, isDragging }) {
  const sortable = useSortable({ id: job.id });
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.5 : 1,
  };

  const status = STATUS_COLORS[job.status] || STATUS_COLORS.assigned;
  const prio = PRIORITY_COLORS[job.priority] || PRIORITY_COLORS.normal;
  const flowIdx = STATUS_FLOW.findIndex((s) => s.value === job.status);
  const next = flowIdx >= 0 && flowIdx < STATUS_FLOW.length - 1 ? STATUS_FLOW[flowIdx + 1] : null;
  const isActive = idx === 0;

  return (
    <div
      ref={sortable.setNodeRef}
      style={style}
      className={cn(
        "flex items-start gap-2 bg-white border rounded-md p-2.5 h-[124px] overflow-hidden",
        isActive ? "border-blue-400 ring-1 ring-blue-100" : "border-slate-200",
        sortable.isDragging && "shadow-lg ring-2 ring-blue-300"
      )}
      data-testid={`queue-item-${job.id}`}
    >
      {/* drag handle + position + nudge buttons */}
      <div className="flex flex-col items-center gap-0.5 shrink-0">
        <button
          {...sortable.attributes}
          {...sortable.listeners}
          className="text-slate-300 hover:text-slate-600 cursor-grab active:cursor-grabbing touch-none p-0.5"
          aria-label="Drag to reorder"
          title="Drag to reorder"
          data-testid={`queue-drag-${job.id}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div
          className={cn(
            "h-7 w-7 rounded-md flex items-center justify-center font-display font-black text-sm",
            isActive ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700"
          )}
          data-testid={`queue-pos-${job.id}`}
        >
          {idx + 1}
        </div>
        <button
          onClick={() => onMove(job, "up")}
          disabled={idx === 0}
          className="text-slate-400 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid={`queue-up-${job.id}`}
          aria-label="Move up in queue"
          title="Move up"
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={() => onMove(job, "down")}
          disabled={idx === total - 1}
          className="text-slate-400 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid={`queue-down-${job.id}`}
          aria-label="Move down in queue"
          title="Move down"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className={cn(
              "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide",
              prio.bg,
              prio.text
            )}
          >
            {job.priority === "urgent" && <Zap className="h-2.5 w-2.5" strokeWidth={3} />}
            {job.priority}
          </span>
          <Badge variant="secondary" className={cn("text-[10px]", status.bg, status.text, "border-0")}>
            <span className={cn("h-1 w-1 rounded-full mr-1", status.dot)} />
            {STATUS_LABEL[job.status]}
          </Badge>
          {isActive && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700">
              NEXT UP
            </span>
          )}
        </div>
        <div className="font-display font-bold text-sm text-slate-900 mt-1 truncate">
          {job.customer_name}
        </div>
        <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-1">
          <MapPin className="h-3 w-3 text-slate-400 shrink-0" />
          <span className="truncate">{job.address}</span>
        </div>
        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
          <Car className="h-3 w-3 text-slate-400 shrink-0" />
          <span className="truncate">{job.vehicle}</span>
          <span className="text-slate-300">·</span>
          <span className="font-semibold">{SERVICE_LABEL[job.service_type]}</span>
        </div>

        {isActive && next && (
          <div className="mt-2 flex flex-wrap gap-1.5" onClick={(e) => e.stopPropagation()}>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => onAdvance(job, next.value)}
              data-testid={`queue-advance-${job.id}`}
            >
              Mark {next.label}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-slate-500 hover:text-rose-700"
              onClick={() => onAdvance(job, "completed")}
              data-testid={`queue-complete-${job.id}`}
            >
              Complete
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-slate-500 hover:text-blue-700"
              onClick={() => onReassign?.(job)}
              data-testid={`queue-reassign-${job.id}`}
              title="Move this job to a different truck"
            >
              <ArrowRightLeft className="h-3 w-3" /> Reassign
            </Button>
          </div>
        )}
        {/* Reassign is normally the active (idx 0) job's prerogative, but
            an orphaned-in-progress job (assigned_truck_id wiped by a
            poller race, see impounds.py WOT0462459 fix) never lands in a
            truck queue at all — so this never renders for them. The
            dispatcher's escape hatch for those lives in the Dashboard's
            In-Progress column: the JobCard's Assign button opens the same
            dialog in reassign mode. */}
      </div>

      <div className="text-xs text-slate-400 flex items-center gap-1 shrink-0">
        <Clock className="h-3 w-3" />
        {formatTimeAgo(job.created_at)}
      </div>
    </div>
  );
}

export default function TruckQueue({ truck, queue, onChange, onReassign, onOpenDetail }) {
  // Local optimistic copy so the UI updates instantly on drop
  const [items, setItems] = useState(queue || []);
  // Feb 2026 — sync on CONTENT change, not just id-list change.
  // The old comparison ("did the set of ids change?") let dispatcher
  // edits to customer_name / address / vehicle / priority / status
  // slip past silently: same ids → no re-sync → the Fleet Assigned
  // card kept rendering the pre-edit snapshot until the next reorder
  // or completion. Fingerprint every displayed field so any visible
  // change on a job we're already rendering triggers a fresh copy.
  const fingerprint = (arr) =>
    (arr || [])
      .map(
        (j) =>
          `${j.id}|${j.status}|${j.customer_name || ""}|${j.address || ""}|` +
          `${j.vehicle || ""}|${j.rego || ""}|${j.priority || ""}|` +
          `${j.service_type || ""}|${j.queue_position ?? ""}`,
      )
      .join("~");
  const queueFp = fingerprint(queue);
  const localFp = fingerprint(items);
  useEffect(() => {
    if (queueFp !== localFp) setItems(queue || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueFp]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const moveItem = async (job, direction) => {
    try {
      await moveJob(job.id, direction);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't reorder");
    }
  };

  const advance = async (job, next) => {
    try {
      await updateJobStatus(job.id, next);
      toast.success(`Marked ${next.replace("_", " ")}`);
      onChange?.();
    } catch {
      toast.error("Update failed");
    }
  };

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex((j) => j.id === active.id);
    const newIdx = items.findIndex((j) => j.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(items, oldIdx, newIdx);
    setItems(reordered); // optimistic
    try {
      await reorderTruckQueue(truck.id, reordered.map((j) => j.id));
      toast.success("Queue reordered");
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save new order");
      setItems(queue || []); // rollback
    }
  };

  if (!items || items.length === 0) {
    return (
      <EmptyState
        icon={ArrowRightLeft}
        title="No jobs queued"
        hint="Drag an assigned job here or use Assign Truck to route the next call."
        className="!py-8"
        testId={`truck-queue-empty-${truck.id}`}
      />
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={items.map((j) => j.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2" data-testid={`truck-queue-${truck.id}`}>
          {items.map((job, idx) => (
            <SortableQueueItem
              key={job.id}
              job={job}
              idx={idx}
              total={items.length}
              onMove={moveItem}
              onAdvance={advance}
              onReassign={onReassign}
              onOpenDetail={onOpenDetail}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
